import { Loader2, AlertTriangle } from 'lucide-react'

/**
 * Analytics / Object Version Status Codes
 *
 * 0  = Not run (grey)
 * 1  = In progress (animated dots)
 * 2  = Red (compromised)
 * 3  = Amber (suspicious)
 * 4  = Green (clean)
 * 10 = Failed (warning triangle)
 */

export type AnalyticsStatus = 0 | 1 | 2 | 3 | 4 | 10

export interface StatusConfig {
    label: string
    color: string        // Tailwind bg class for dots
    textColor: string    // Tailwind text class
    ringColor: string    // Tailwind ring class
    hex: string          // Hex colour for visualisation
    pulse: string        // Pulse animation class (empty = no pulse)
    type: 'dot' | 'spinner' | 'icon'  // How to render
}

const STATUS_MAP: Record<number, StatusConfig> = {
    0: {
        label: 'Not Run',
        color: 'bg-gray-300 dark:bg-gray-600',
        textColor: 'text-gray-400',
        ringColor: 'ring-gray-300',
        hex: '#CCCCCC',
        pulse: '',
        type: 'dot',
    },
    1: {
        label: 'In Progress',
        color: 'bg-blue-400',
        textColor: 'text-blue-500',
        ringColor: 'ring-blue-300',
        hex: '#60A5FA',
        pulse: 'animate-pulse',
        type: 'spinner',
    },
    2: {
        label: 'Compromised',
        color: 'bg-red-500',
        textColor: 'text-red-500',
        ringColor: 'ring-red-300',
        hex: '#FFB3B3',
        pulse: 'animate-analytics-pulse-red',
        type: 'dot',
    },
    3: {
        label: 'Suspicious',
        color: 'bg-amber-500',
        textColor: 'text-amber-500',
        ringColor: 'ring-amber-300',
        hex: '#FFD699',
        pulse: 'animate-analytics-pulse-amber',
        type: 'dot',
    },
    4: {
        label: 'Clean',
        color: 'bg-green-500',
        textColor: 'text-green-500',
        ringColor: 'ring-green-300',
        hex: '#B4E197',
        pulse: '',
        type: 'dot',
    },
    10: {
        label: 'Failed',
        color: 'bg-yellow-500',
        textColor: 'text-yellow-600',
        ringColor: 'ring-yellow-300',
        hex: '#F59E0B',
        pulse: '',
        type: 'icon',
    },
}

const DEFAULT_STATUS = STATUS_MAP[0]

/**
 * Get the full status config for an analytics code
 */
export function getAnalyticsConfig(status: number | null | undefined): StatusConfig {
    if (status == null) return DEFAULT_STATUS
    return STATUS_MAP[status] || DEFAULT_STATUS
}

/**
 * Get the colour class for a status dot
 */
export function getAnalyticsColor(status: number | null | undefined): string {
    return getAnalyticsConfig(status).color
}

/**
 * Get the hex colour for visualisation nodes
 */
export function getAnalyticsHex(status: number | null | undefined): string {
    return getAnalyticsConfig(status).hex
}

/**
 * Get the status label
 */
export function getAnalyticsLabel(status: number | null | undefined): string {
    return getAnalyticsConfig(status).label
}

/**
 * Render a status indicator — dot, spinner, or warning icon
 * This is the single source of truth for analytics status display.
 *
 * @param status - Analytics status code (0, 1, 2, 3, 4, 10)
 * @param size - Size class (default: 'h-3 w-3')
 * @param showRing - Whether to show the outer ring (default: false)
 */
export function AnalyticsStatusIndicator({
    status,
    size = 'h-3 w-3',
    showRing = false,
}: {
    status: number | null | undefined
    size?: string
    showRing?: boolean
}) {
    const config = getAnalyticsConfig(status)

    // In Progress — animated dots
    if (config.type === 'spinner') {
        return (
            <span className={`inline-flex items-center gap-[2px] ${size}`} title={config.label}>
                <span className={`rounded-full bg-blue-500 animate-bounce`} style={{ width: '4px', height: '4px', animationDelay: '0ms' }} />
                <span className={`rounded-full bg-blue-500 animate-bounce`} style={{ width: '4px', height: '4px', animationDelay: '150ms' }} />
                <span className={`rounded-full bg-blue-500 animate-bounce`} style={{ width: '4px', height: '4px', animationDelay: '300ms' }} />
            </span>
        )
    }

    // Failed — warning triangle
    if (config.type === 'icon') {
        return (
            <AlertTriangle className={`${size} text-yellow-600`} title={config.label} />
        )
    }

    // Standard dot (Not Run, Red, Amber, Green)
    return (
        <span className="relative inline-flex" title={config.label}>
            {showRing && (config.pulse) && (
                <span className={`absolute inset-0 rounded-full ${config.color} opacity-30 ${config.pulse}`} />
            )}
            <span className={`${size} rounded-full ${config.color} ${config.pulse}`} />
        </span>
    )
}
